import React, { useState } from 'react'
import { FieldErrorsImpl, useFormContext, useFormState } from 'react-hook-form'
import { Stack } from '@mantine/core'
import get from 'lodash/get'

import { AttendeeCardEdit } from './AttendeeCardEdit'
import { AttendeeCardView } from './AttendeeCardView'

import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { useScriptState } from '@/components/Event/NewScript/hooks/state/useScriptState'
import { useEventZoomParticipants } from '@/components/Event/NewScript/queries/scriptData'
import {
  omitIdentityDocumentErrors,
  omitWitnessLocationErrors,
} from '@/components/Event/NewScript/utils/attendees'
import { Event, ScriptAttendee } from '@/types'
import { getEventTimezone } from '@/utils/time'

type Props = {
  attendee: ScriptAttendee
  onSubmitClick?: (attendee: ScriptAttendee) => void
  emailEmptySlot?: React.ReactNode
  event: Event
  isWitnessLocationEnabled?: boolean
  isWithZoomParticipants?: boolean
  namePrefix: string
  allAttendees: ScriptAttendee[]
}

export const AttendeeCard: React.FC<Props> = ({
  attendee,
  onSubmitClick,
  emailEmptySlot,
  children,
  event,
  isWitnessLocationEnabled = false,
  isWithZoomParticipants = true,
  namePrefix,
  allAttendees,
}) => {
  const {
    actions: { updateAttendee },
  } = useScriptState()
  const { data: eventParticipants } = useEventZoomParticipants(event, {
    enabled: isWithZoomParticipants,
  })
  const [edit, setEdit] = useState<boolean>(!attendee.name)
  const { control } = useFormContext()
  const { errors } = useFormState({ control })

  // The step's own validation reports here. Rendered on the collapsed card so a
  // bad value is explained where the reporter is pressing the action button,
  // instead of leaving that button silently doing nothing.
  const attendeeErrors = omitWitnessLocationErrors(
    omitIdentityDocumentErrors(
      get(errors, namePrefix) as FieldErrorsImpl<ScriptAttendee> | undefined,
    ),
    isWitnessLocationEnabled,
  )

  const onEditClickHandler = () => {
    setEdit(true)
  }

  const onSubmitClickHandler = (attendee: ScriptAttendee) => {
    if (onSubmitClick) {
      onSubmitClick(attendee)
    } else {
      updateAttendee(attendee)
    }
    setEdit(false)
  }

  const onCancelClick = () => {
    setEdit(false)
  }

  return (
    <Stack>
      <ActionContainer>
        <Stack>
          {edit ? (
            <AttendeeCardEdit
              isWitnessLocationEnabled={isWitnessLocationEnabled}
              timezone={getEventTimezone(event)}
              eventType={event.type}
              attendee={attendee}
              zoomParticipants={
                isWithZoomParticipants && eventParticipants
                  ? eventParticipants
                  : []
              }
              namePrefix={namePrefix}
              allAttendees={allAttendees}
              onSubmitClick={onSubmitClickHandler}
              onCancelClick={onCancelClick}
            />
          ) : (
            <AttendeeCardView
              attendee={attendee}
              onEditClick={onEditClickHandler}
              emailEmptySlot={emailEmptySlot}
              errors={attendeeErrors}
            />
          )}
          {children}
        </Stack>
      </ActionContainer>
    </Stack>
  )
}
